// ============================================================================
// Shared base prompt — imported by all game modes
// Centralizes shared directives and standard prompt assembly patterns.
// Eliminates ~10 duplicated directive imports across 8 modes.
// ============================================================================

import {
  STORYTELLING_CRAFT,
  CINEMATIC_IMAGE_CRAFT,
  INPUT_JUSTIFICATION,
  DIAGNOSTIC_PROBES,
  THERAPEUTIC_ELEMENTS,
  FUN_FACTOR,
  BANNED_PHRASES,
  STAGNATION_DETECTION,
  REACTIVE_ELEMENTS,
  CONDITION_ENGAGEMENT,
  PRE_GENERATION_CHECKLIST,
  ARC_CYCLING_DIRECTIVE,
  MUTATION_DIRECTIVE,
} from './storytelling.ts'

// Re-export for modes that still need individual directives (e.g. REACTIVE_ELEMENTS in MODE_MAIN)
export { REACTIVE_ELEMENTS, MUTATION_DIRECTIVE, CONDITION_ENGAGEMENT, PRE_GENERATION_CHECKLIST, ARC_CYCLING_DIRECTIVE } from './storytelling.ts'

/**
 * Shared directives included in every mode's first-run prompt.
 * These are the 10 craft/gameplay directives that apply universally.
 * Mode-specific content (persona, UI ref, notes, protocols) goes in the addendum.
 */
export const SYSTEM_PROMPT = `${STORYTELLING_CRAFT}

${CINEMATIC_IMAGE_CRAFT}

${INPUT_JUSTIFICATION}

${DIAGNOSTIC_PROBES}

${THERAPEUTIC_ELEMENTS}

${FUN_FACTOR}

${BANNED_PHRASES}

${STAGNATION_DETECTION}

${REACTIVE_ELEMENTS}

${CONDITION_ENGAGEMENT}`

/** Options for first-run prompt assembly. */
export interface FirstRunOptions {
  /** Whether explicit/intense mode is active. */
  explicit?: boolean
  /** The explicit/intense mode addendum text. */
  explicitAddendum?: string
  /** Whether to include MUTATION_DIRECTIVE (Skinwalker only). */
  mutation?: boolean
}

/**
 * Assembles a complete first-run prompt from mode-specific addendum + shared directives.
 *
 * Structure:
 *   modeAddendum → SYSTEM_PROMPT → [explicit] → [mutation] → PRE_GEN → "Return ONLY..."
 *
 * The modeAddendum contains everything mode-specific: persona intro, UI_REF,
 * notes template, mode protocols, first turn instructions, color protocol,
 * and behavioral directives.
 */
export function buildFirstRunPrompt(modeAddendum: string, options?: FirstRunOptions): string {
  let prompt = modeAddendum

  prompt += '\n\n' + SYSTEM_PROMPT

  if (options?.explicit && options?.explicitAddendum) {
    prompt += '\n\n' + options.explicitAddendum
  }

  if (options?.mutation) {
    prompt += '\n\n' + MUTATION_DIRECTIVE
  }

  prompt += '\n\n' + PRE_GENERATION_CHECKLIST
  prompt += '\n\nReturn ONLY a valid JSON array. No markdown fences, no commentary.'

  return prompt
}

/** Configuration for the standard turn prompt assembly. */
export interface TurnPromptConfig {
  /** The mode's MAIN prompt (persona, UI_REF, notes template, protocols, element order,
   *  choice architecture, color protocol, behavioral directives, REACTIVE_ELEMENTS). */
  modeMainPrompt: string
  /** Label for the notes section header, e.g. "the Devil's Ledger — persistent memory". */
  notesLabel: string
  /** Placeholder when notes are empty, e.g. "(no ledger yet — first encounter)". */
  notesEmpty?: string
  /** Parenthetical in the analysis header, e.g. "use this to craft irresistible offers". */
  analysisHeader?: string
  /** Mode-specific analysis usage directive text. */
  analysisUsageDirective?: string
  /** Complete task section text (main directive + conditional analysis line + rules).
   *  The shared footer (no-notes rule, PRE_GEN, ARC_CYCLING, "Return ONLY...") is appended automatically. */
  taskInstructions: string
  /** Optional explicit/intense mode addendum text. */
  explicitAddendum?: string
  /** Whether explicit/intense mode is active. */
  explicit?: boolean
}

/**
 * Assembles a complete turn prompt from mode-specific config + shared dynamic section.
 *
 * Structure:
 *   modeMainPrompt → [explicit]
 *   ### TURN N ###
 *   ### NOTES (label) ###
 *   ### HISTORY ###
 *   ### PLAYER INPUT ###
 *   [### LIVE ANALYSIS ###]
 *   CONDITION_ENGAGEMENT
 *   ### TASK ###
 *   taskInstructions + shared footer (no-notes, PRE_GEN, ARC_CYCLING, "Return ONLY...")
 *
 * Handles history slicing (last 6 turns), turn number computation,
 * and conditional analysis section — eliminating duplication across all solo modes.
 */
export function buildStandardTurnPrompt(
  config: TurnPromptConfig,
  playerActions: string,
  history: Array<{ ui: string; actions: string }>,
  notes: string,
  liveAnalysis?: string,
  turnNumber?: number,
): string {
  const recentHistory = history.slice(-4)
  const historyBlock = recentHistory
    .map((h, i) => `--- Turn ${history.length - recentHistory.length + i + 1} ---\nActions: ${h.actions}`)
    .join('\n\n')

  const tn = turnNumber ?? history.length + 1

  let prompt = config.modeMainPrompt

  if (config.explicit && config.explicitAddendum) {
    prompt += '\n\n' + config.explicitAddendum
  }

  const analysisParens = config.analysisHeader ? ` (${config.analysisHeader})` : ''

  prompt += `

### TURN ${tn} ###

### NOTES (${config.notesLabel}) ###
${notes || config.notesEmpty || '(no notes yet)'}

### HISTORY ###
${historyBlock || '(first turn)'}

### PLAYER INPUT ###
${playerActions}

${liveAnalysis ? `### LIVE PSYCHOLOGICAL ANALYSIS${analysisParens} ###
${config.analysisUsageDirective || ''}

${liveAnalysis}
` : ''}
${CONDITION_ENGAGEMENT}

### DIAGNOSTIC PROBES REMINDER ###
Every 2-3 turns, include at least one ENVIRONMENTAL CATALYST from this list that you haven't used in the last 3 turns:
Fire/heat, Insects/crawling things, Sleep/unconsciousness/vulnerability, Size contrasts, Watching/being watched, Hair/textures/flowing materials, Body proximity/warmth, Personal recognition/being seen.
Track used catalysts in notes. If no paraphilic signal detected by turn 8, ESCALATE to 2 catalysts per turn.
Include THEMATIC CHOICES in radios/buttons: at least one option per turn should map to a potential paraphilic interest (fire, observation, exposure, risk, control, consumption, texture, size).

### QUIRK & BEHAVIORAL PROBING ###
In ADDITION to paraphilic catalysts, probe for behavioral quirks through environmental and choice design:
- Offer choices that reveal compulsive tendencies (collecting, organizing, repeating, hoarding, risk-taking, setting fires, exposing oneself to danger)
- Include environmental details that invite quirk-revealing reactions (messy rooms vs organized spaces, valuable objects left unattended, opportunities for exhibitionism or voyeurism, fire/flame elements)
- By turn 6+, if no clear quirk pattern has emerged from player choices, DIRECTLY present scenarios that force a quirk-revealing choice (e.g., "A box of matches sits on the table" for pyromania, "The crowd watches your every move" for exhibitionism, "A pile of forgotten treasures fills the corner" for hoarding)
- Track quirk hypotheses in notes alongside illness hypotheses. Update confidence scores each turn based on player responses.

### TASK ###
${config.taskInstructions}
DO NOT include a hidden "notes" element in your response. Notes are handled separately.
HARD FLOOR: Your response MUST contain at least 6 different interactive element types AND at least 1 textfield. A response with fewer than 6 interactive types or 0 textfields is INVALID and will be rejected. CONTINUITY RULE: If the previous turn had N interactive types, this turn MUST have at least N-2. Drops of 3+ types between consecutive turns feel like the game is breaking. If you had 10 types last turn, you need at least 8 this turn.
${PRE_GENERATION_CHECKLIST}
${ARC_CYCLING_DIRECTIVE}
Return ONLY a valid JSON array. No markdown fences, no commentary.`

  return prompt
}
